import { useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import { Alert, Button, Col, Form, FormFeedback, FormGroup, Input, Label, Modal, Row } from 'reactstrap';
import { IUser } from 'src/services/types';
import { useFormik } from 'formik';
import * as Yup from "yup";
import { updateUser } from 'src/services/users-service';
import { Eye, EyeOff} from 'feather-icons-react';

interface IProps {
    show: boolean;
    onClose: () => void;
    item: IUser;
    onRefresh: () => void;
} 
export const EditUserModal = ({ show, onClose, item, onRefresh }: IProps) => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const formik = useFormik({
        enableReinitialize: true,

        initialValues: {
            email: item?.email || '',
            password: '',
        },
        validationSchema: Yup.object().shape({
            email: Yup.string().email("Invalid email format").required("Email is required"),
            password: Yup.string().optional().min(8, "Password must be at least 8 characters"),
        }),
        onSubmit: (values) => {
            onUpdateEmailPassword(values?.email, values?.password || '');
        }
    });

    const onUpdateEmailPassword = async (email: string, password: string)=> {
        setLoading(true);
        setErrorMessage('');
        let data: {email?: string, password?: string } = {}
        if (password) {
            data['password'] = password;
        }
        if(item?.email !== email) {
            data['email'] = email;
        }
        if(Object.keys(data).length === 0) {
            toast('No changes made', { type: 'info', autoClose: 3000 });
            setLoading(false);
            return;
        }
        const res = await updateUser(item?.id, data);
        setLoading(false);
        if (res.result === 'ok') {
            toast( 'User updated successfully', { type: 'success', autoClose: 3000});
            onRefresh();
            onClose();
        } else {
            setErrorMessage(res.message);
            console.log('error:', res.message);
        }
    }

    useEffect(() => {
        if(show) {
            formik.resetForm();
        }
    }, [show]);

  return (
    <Modal
        isOpen={show}
        backdrop={"static"}
        id="staticBackdrop"
        className=''
    >
        {
            <div style={{position: 'relative', backgroundColor: loading ? '#D8C8B8' : '#fff' }}>
                <div className="modal-header">
                    <h5 className="modal-title" id="staticBackdropLabel">
                        Update User
                    </h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={onClose}
                        aria-label="Close"
                    ></button>
                </div>
                <Form className="needs-validation"
                        onSubmit={(e) => {
                            e.preventDefault();
                            formik.handleSubmit();
                            return false;
                        }}
                >
                    <div className="modal-body">
                        <Row>
                            <Col md="12">
                                <FormGroup className="mb-3">
                                    <Label htmlFor="email">Email <small className='text-muted'>(Optional)</small></Label>
                                    <Input
                                        name="email"
                                        placeholder="Enter a email..."
                                        type="email"
                                        className="form-control"
                                        id="email"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.email || ""}
                                        invalid={
                                            formik.touched.email && formik.errors.email ? true : false
                                        }
                                        style={{ backgroundColor: '#fff' }}
                                    />
                                    {formik.touched.email && formik.errors.email ? (
                                        <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
                                    ) : null}
                                </FormGroup>
                            </Col>
                            <Col md="12">
                                <FormGroup className="mb-3">
                                    <Label htmlFor="password">Password <small className='text-muted'>(Optional)</small></Label>
                                    <div className='d-flex justify-content-center align-items-start'>
                                        <div style={{ flex: 1 }}>
                                            <Input
                                                name="password"
                                                placeholder="Enter a password..."
                                                type={showPassword ? "text" : "password"}
                                                className="form-control"
                                                id="password"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.password || ""}
                                                invalid={
                                                    formik.touched.password && formik.errors.password ? true : false
                                                }
                                                style={{ backgroundColor: '#fff' }}
                                            />
                                            {formik.touched.password && formik.errors.password ? (
                                                <FormFeedback type="invalid">{formik.errors.password}</FormFeedback>
                                            ) : null}
                                        </div>
                                        <div className=''>
                                            {
                                                <div 
                                                    className='cursor-pointer' 
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    style={{ backgroundColor: '#f7f7f7', padding: '0.4rem 0.5rem', borderRadius: '0.25rem', border: '1px solid #ced4da' }}
                                                >
                                                    {!showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </FormGroup>
                            </Col>
                        </Row>

                        <div>
                            <Alert color="danger" isOpen={!!errorMessage} toggle={()=>setErrorMessage('')} >
                                {errorMessage}
                            </Alert> 
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={onClose}
                        >
                            Close
                        </button>
                        
                        <Button color="primary" type="submit" className='waves-effect waves-light'>
                            Update User
                        </Button>
                    </div>
                </Form>
                {
                    loading ? 
                    <div 
                        className='d-flex justify-content-center align-items-center rounded my-2 mx-2' 
                        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(253, 252, 255, 0.75)'}}
                    >
                         <div className="spinner-border text-primary" role="status">
                        </div>
                        <span className="text-primary fs-5 mx-2">
                            Updating User...
                        </span>
                    </div>
                    : null
                }
            </div>   
        }
    </Modal>
  )
}
